/**
 * dual-camera.js — «Две камеры» for /webcam-recorder (2026-10-20).
 *
 * The ask: a phone films the scene with the back camera while the front camera
 * puts the person in a bubble (circle / square / rectangle / half the frame) —
 * the Reels clip «pointing at my monitor, my face in a circle».
 *
 * What a browser allows (measured 2026-10-20, memory
 * reference_dual_camera_in_the_browser_2026_10_20):
 *   • iPhone / iPad — never, in any browser: WebKit ends the first camera's
 *     track the moment a second one opens (canCaptureFromMultipleCameras() is
 *     false on iOS; bugs.webkit.org 238492, open since 2022).
 *   • Android — the ENGINE allows it: Chrome 133 and Yandex 26.8 on an Android 16
 *     emulator delivered frames from both cameras at once. The phone's camera
 *     decides: many models refuse the second camera («Could not start video
 *     source») or freeze the first.
 *   • Desktop — two webcams open side by side.
 * So the only verdict is a PROBE on the person's own device, and «the second
 * getUserMedia resolved» is not one: /camera-test logged «ok» 6 of 6 on iPhones
 * where the first camera had gone dark. The probe counts FRAMES from both.
 *
 * When the phone cannot, the person still gets the clip:
 *   • «Сначала сцена, потом лицо» — the scene is filmed first, then the person
 *     records themselves while it plays (sceneAudioTrack + the compositor's
 *     mainElement) — works on any phone, iPhone included;
 *   • «Второй телефон — камера» — another device joins by QR and sends its
 *     camera (remoteHost / remoteSender over room-connect; own devices, so the
 *     relay rules of interpersonal tools do not apply — no __NO_TURN);
 *   • two finished clips → /split-screen.
 *
 * API (window.WrDualCamera):
 *   isIOS()                                    — iPhone/iPad (incl. iPadOS desktop UA)
 *   openSecond({mainStream, deviceId}) → MediaStream   the other camera, small (640×480)
 *   probe(mainStream, secondStream, {ms}) → {ok, fa, fb, ms, why}
 *   watchTrack(track, onGone) → stop()         ended / muted for > 2 s
 *   antiFlicker(track, on) → Promise<{supported, applied, exposureTime}>   (through __trackConstraint)
 *   sceneAudioTrack(videoEl) → {track, wake(), release()} | null
 *   remoteHost({prefix, onStream, onStatus}) → {code, url, stop()}
 *   remoteSender({code, prefix, video, onStatus}) → {flip(), stop()}
 *   deviceModel() → Promise<string>
 */
(function () {
    'use strict';
    if (window.WrDualCamera) return;

    var ua = navigator.userAgent || '';
    function isIOS() { return /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1); }
    function isMobile() { return /android|iphone|ipad|ipod/i.test(ua) || isIOS(); }

    function settingsOf(stream) {
        try { return stream.getVideoTracks()[0].getSettings() || {}; } catch (_) { return {}; }
    }

    // A camera opens with a plain getUserMedia, as the probe expects it: the
    // exact camera or nothing, no relaxed retries and no help bubble of its own
    // (the page answers a refusal with the ways out). A call that never answers
    // is ended by the page-wide safety net in timbrica-core.js (12/20/60 s by
    // permission state). What the camera door gives here is the WORDS: its
    // classifier (denied / not_found / busy / unresponsive …) and its sentence
    // for the cause in the page language — explain().
    function openCamera(video) {
        return navigator.mediaDevices.getUserMedia({ video: video, audio: false });
    }
    function explain(e) {
        var code = '', msg = '';
        try {
            if (window.CameraManager && typeof window.CameraManager.classify === 'function') {
                code = window.CameraManager.classify(e);
                msg = new window.CameraManager({ slug: 'webcam-recorder' }).getErrorMessage(code);
            }
        } catch (_) {}
        if (!code) {
            var n = (e && e.name) || '';
            code = n === 'NotAllowedError' ? 'denied' : (n === 'NotFoundError' || n === 'OverconstrainedError') ? 'not_found'
                : n === 'NotReadableError' ? 'busy' : n === 'TimeoutError' ? 'unresponsive' : 'generic';
        }
        var out = new Error(msg || (e && e.message) || 'camera');
        out.code = code; out.causeName = (e && e.name) || ''; out.originalError = e || null;
        return out;
    }

    // Which way a camera looks: the device's own capabilities (Chrome), else
    // its label («camera2 1, facing front», «Back Ultra Wide Camera»).
    function facingOf(d) {
        try {
            var c = d.getCapabilities ? d.getCapabilities() : null;
            var f = c && c.facingMode;
            if (f && f.length) return String(f[0]);
        } catch (_) {}
        var l = String(d.label || '');
        if (/front|user|selfie|фронт/i.test(l)) return 'user';
        if (/back|rear|environment|задн|тыл/i.test(l)) return 'environment';
        return '';
    }

    // The other camera: on a phone the opposite facing, on a desktop another
    // device. Small on purpose — it becomes a bubble, and two full-size
    // streams plus an encoder heat a phone fast.
    // ⚠️ On a phone only cameras of the OTHER side are candidates: a phone
    // with a main and an ultra-wide back camera would otherwise hand the
    // bubble a second view of the scene when the front one refuses — the
    // probe passes (frames flow) and the person is not in the clip.
    async function openSecond(o) {
        o = o || {};
        var main = settingsOf(o.mainStream);
        var base = { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } };
        var tries = [];
        var mobile = isMobile();
        var want = main.facingMode === 'user' ? 'environment' : 'user';
        if (o.deviceId) tries.push(Object.assign({ deviceId: { exact: o.deviceId } }, base));
        if (mobile) tries.push(Object.assign({ facingMode: { exact: want } }, base));
        try {
            var devs = await navigator.mediaDevices.enumerateDevices();
            devs.filter(function (d) {
                if (d.kind !== 'videoinput' || !d.deviceId || d.deviceId === main.deviceId) return false;
                return !mobile || facingOf(d) === want;
            }).forEach(function (d) { tries.push(Object.assign({ deviceId: { exact: d.deviceId } }, base)); });
        } catch (_) {}
        var last = null;
        for (var i = 0; i < tries.length; i++) {
            try { return await openCamera(tries[i]); }
            catch (e) { last = e; }
        }
        // the cause for the field: busy / not_found / unresponsive … in the
        // camera door's terms, plus the DOMException's own name
        var why = last ? explain(last) : null;
        var err = new Error((last && last.message) || 'no-second-camera');
        err.name = (why && why.code) || 'not_found';
        err.causeName = (last && last.name) || '';
        err.code = 'no_second';
        throw err;
    }

    // Frames that ARRIVE from a track during `ms`. A processor reads a CLONE
    // (cancelling a reader ends the track it reads); without one (Firefox,
    // Safari) a nearly invisible <video> counts presented frames.
    function countFrames(track, ms) {
        return new Promise(function (resolve) {
            var n = 0, done = false;
            if (typeof window.MediaStreamTrackProcessor === 'function') {
                var clone = track.clone();
                var reader = null;
                try { reader = new MediaStreamTrackProcessor({ track: clone }).readable.getReader(); } catch (_) {}
                if (reader) {
                    (async function pump() {
                        while (!done) {
                            var r;
                            try { r = await reader.read(); } catch (_) { break; }
                            if (r.done) break;
                            try { r.value.close(); } catch (_) {}
                            n++;
                        }
                    })();
                    setTimeout(function () {
                        done = true;
                        try { reader.cancel().catch(function () {}); } catch (_) {}
                        try { clone.stop(); } catch (_) {}
                        resolve(n);
                    }, ms);
                    return;
                }
                try { clone.stop(); } catch (_) {}
            }
            var v = document.createElement('video');
            v.muted = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.autoplay = true;
            v.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;opacity:.01;pointer-events:none';
            v.srcObject = new MediaStream([track]);
            document.body.appendChild(v);
            var p = v.play(); if (p && p.catch) p.catch(function () {});
            var lastT = -1, iv = 0;
            if (v.requestVideoFrameCallback) {
                var cb = function () { n++; if (!done) v.requestVideoFrameCallback(cb); };
                v.requestVideoFrameCallback(cb);
            } else {
                iv = setInterval(function () { if (v.currentTime !== lastT) { n++; lastT = v.currentTime; } }, 40);
            }
            setTimeout(function () {
                done = true;
                if (iv) clearInterval(iv);
                try { v.pause(); v.srcObject = null; if (v.parentNode) v.parentNode.removeChild(v); } catch (_) {}
                resolve(n);
            }, ms);
        });
    }

    // Both cameras must deliver frames AT THE SAME TIME, and the first must
    // still be alive after the second opened. Three frames each is a camera;
    // none is a camera that froze. A busy phone (the page still loading, the
    // preview being drawn) READS fewer frames than its cameras give — measured
    // on a loaded machine 2026-10-21: 0–12 frames in 1.8 s from cameras that
    // run at 30 — so below the bar the probe takes one more, longer look
    // (o.extraMs, 2.2 s) before it says no.
    async function probe(mainStream, secondStream, o) {
        o = o || {};
        var ms = o.ms || 1800, extra = o.extraMs == null ? 2200 : o.extraMs;
        var a = mainStream && mainStream.getVideoTracks()[0];
        var b = secondStream && secondStream.getVideoTracks()[0];
        if (!a || !b) return { ok: false, fa: 0, fb: 0, ms: 0, why: 'no_track' };
        var aMuted = false;
        var onMute = function () { aMuted = true; };
        try { a.addEventListener('mute', onMute); } catch (_) {}
        var t0 = performance.now();
        var res = await Promise.all([countFrames(a, ms), countFrames(b, ms)]);
        var fa = res[0], fb = res[1];
        if ((fa < 3 || fb < 3) && extra > 0 && a.readyState === 'live' && b.readyState === 'live') {
            var more = await Promise.all([countFrames(a, extra), countFrames(b, extra)]);
            fa += more[0]; fb += more[1];
        }
        try { a.removeEventListener('mute', onMute); } catch (_) {}
        var why = '';
        if (a.readyState !== 'live') why = 'main_ended';
        else if (b.readyState !== 'live') why = 'second_ended';
        else if (aMuted) why = 'main_muted';
        else if (fa < 3) why = 'main_frozen';
        else if (fb < 3) why = 'second_frozen';
        return { ok: !why, fa: fa, fb: fb, ms: Math.round(performance.now() - t0), why: why };
    }

    // A camera gone mid-take: `ended`, or muted for more than two seconds (a
    // call, another app, thermal shutdown — the track stays «live» and silent).
    function watchTrack(track, onGone) {
        if (!track) return function () {};
        var t = 0, fired = false;
        function gone(why) { if (fired) return; fired = true; try { onGone(why); } catch (_) {} }
        function onEnded() { gone('ended'); }
        function onMute() { if (!t) t = setTimeout(function () { gone('muted'); }, 2000); }
        function onUnmute() { if (t) { clearTimeout(t); t = 0; } }
        track.addEventListener('ended', onEnded);
        track.addEventListener('mute', onMute);
        track.addEventListener('unmute', onUnmute);
        return function stop() {
            if (t) clearTimeout(t);
            track.removeEventListener('ended', onEnded);
            track.removeEventListener('mute', onMute);
            track.removeEventListener('unmute', onUnmute);
        };
    }

    // Filming a monitor: its refresh beats with the camera's rolling shutter
    // and the picture runs with bands. An exposure of exactly one refresh
    // period integrates every line over a whole frame of the screen, so the
    // bands go. Screens refresh at 60 Hz whatever the mains are (that is
    // 1/60 s); room lighting on 50 Hz mains would want 1/100 — the toggle is
    // named for the screen. Only where the browser exposes a manual shutter
    // (Image Capture constraints — Chrome on Android, some webcams); the page
    // offers the toggle nowhere else. Not yet measured on real phones: the
    // field answer is whether people keep it on (dual_antiflicker telemetry).
    // exposureTime is in units of 100 µs (W3C Image Capture).
    async function antiFlicker(track, on) {
        var caps = {};
        try { caps = (track && track.getCapabilities) ? track.getCapabilities() : {}; } catch (_) {}
        var modes = caps.exposureMode || [];
        var hasManual = modes.indexOf && modes.indexOf('manual') !== -1 && caps.exposureTime;
        if (!hasManual) return { supported: false, applied: false };
        // The one door to camera constraints (timbrica-core.js): one call in
        // flight per track, a muted moment waited out instead of refused.
        var door = window.__trackConstraint;
        if (typeof door !== 'function') return { supported: true, applied: false, err: 'no_door' };
        if (!on) {
            await door(track, { exposureMode: modes.indexOf('continuous') !== -1 ? 'continuous' : 'manual' });
            return { supported: true, applied: false };
        }
        var want = 167; // 1/60 s
        var min = caps.exposureTime.min || 1, max = caps.exposureTime.max || want;
        var val = Math.max(min, Math.min(max, want));
        var r = await door(track, { exposureMode: 'manual', exposureTime: val });
        return r && r.ok ? { supported: true, applied: true, exposureTime: val }
            : { supported: true, applied: false, err: (r && (r.reason || (r.error && r.error.name))) || '' };
    }

    // The scene's sound as a track for the take («сначала сцена, потом лицо»).
    // The element's audio goes ONLY into the recording graph — not to the
    // speakers — so the microphone does not catch it a second time.
    function sceneAudioTrack(videoEl) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC || !videoEl) return null;
        try {
            var ctx = new AC();
            var src = ctx.createMediaElementSource(videoEl);
            var dst = ctx.createMediaStreamDestination();
            src.connect(dst);
            var tr = dst.stream.getAudioTracks()[0] || null;
            return {
                track: tr,
                ctx: ctx,
                // wake inside the press that starts the take; a refusal is not an error worth a rejection
                wake: function () {
                    try {
                        if (ctx.state === 'running') return Promise.resolve();
                        if (typeof window.__safeResume === 'function') return Promise.resolve(window.__safeResume(ctx));
                        return ctx.resume().catch(function () {});
                    } catch (_) { return Promise.resolve(); }
                },
                release: function () { try { src.disconnect(); } catch (_) {} try { ctx.close(); } catch (_) {} }
            };
        } catch (_) { return null; }
    }

    // ── «Второй телефон — камера»: this page hosts, the phone joins by QR ──
    function remoteHost(o) {
        o = o || {};
        var RC = window.__RoomConnect;
        if (!RC || typeof RC.hostRoom !== 'function') throw new Error('room-connect-missing');
        var prefix = o.prefix || 'wrcam-';
        var code = RC.generateCode();
        function st(s, x) { try { if (o.onStatus) o.onStatus(s, x); } catch (_) {} }
        var room = RC.hostRoom({
            prefix: prefix, code: code, maxGuests: 1, acceptMedia: true,
            enableVisibilityRecovery: true, tool: 'webcam-recorder',
            onPeerOpen: function () { st('waiting'); },
            onGuestConnect: function (conn) {
                st('joined');
                try { conn.on('close', function () { st('lost'); }); } catch (_) {}
            },
            onIncomingCall: function (call) {
                try { call.answer(); } catch (_) {}
                call.on('stream', function (s) { st('live'); try { if (o.onStream) o.onStream(s); } catch (_) {} });
                call.on('close', function () { st('lost'); });
            },
            onError: function (e) { st('error', e); }
        });
        var url = location.origin + location.pathname + '?remote-cam=' + encodeURIComponent(code);
        return { code: code, url: url, stop: function () { try { room.destroy(); } catch (_) {} } };
    }

    // The phone that became a camera.
    function remoteSender(o) {
        o = o || {};
        var RC = window.__RoomConnect;
        if (!RC || typeof RC.joinRoom !== 'function') throw new Error('room-connect-missing');
        var stream = null, room = null, call = null, facing = 'user';
        function st(s, x) { try { if (o.onStatus) o.onStatus(s, x); } catch (_) {} }
        // One camera at a time: most phones cannot start the other camera while
        // this one is open, so the old stream goes first. A refusal comes back
        // explained (err.code + the sentence in the page language).
        async function open() {
            var video = { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
            if (stream) { try { stream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {} stream = null; }
            try { return await openCamera(video); } catch (e) { throw explain(e); }
        }
        function show() { if (o.video && stream) { o.video.srcObject = stream; var p = o.video.play(); if (p && p.catch) p.catch(function () {}); } }
        async function sendCurrent() {
            var nt = stream && stream.getVideoTracks()[0];
            var pc = call && (call.peerConnection || call._pc);
            var sender = pc && pc.getSenders ? pc.getSenders().filter(function (x) { return x.track && x.track.kind === 'video'; })[0] : null;
            if (sender && nt) await sender.replaceTrack(nt);
        }
        (async function () {
            try { stream = await open(); }
            catch (e) { st('camera_error', e); return; }
            show();
            room = RC.joinRoom({
                prefix: o.prefix || 'wrcam-', code: o.code, timeoutMs: 20000, enableVisibilityRecovery: true, tool: 'webcam-recorder',
                initiateMediaCall: function () { return stream; },
                onMediaCall: function (c) { call = c; },
                onConnected: function () { st('connected'); },
                onRoomNotFound: function () { st('not_found'); },
                onError: function (e) { st('error', e); }
            });
        })();
        return {
            flip: async function () {
                var was = facing;
                facing = facing === 'user' ? 'environment' : 'user';
                try { stream = await open(); }
                catch (_) {
                    // the other camera refused — back to the one that worked
                    facing = was;
                    try { stream = await open(); show(); await sendCurrent(); } catch (e2) { st('camera_error', e2); }
                    return false;
                }
                show();
                try { await sendCurrent(); } catch (_) {}
                return true;
            },
            stop: function () {
                try { if (room) room.destroy(); } catch (_) {}
                try { if (stream) stream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
            }
        };
    }

    // Phone model for the probe's field telemetry: which phones give two
    // cameras is a question only the field answers. Chrome reduced its UA
    // («Android 10; K»), the model lives in the high-entropy hints.
    function deviceModel() {
        try {
            if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
                return navigator.userAgentData.getHighEntropyValues(['model']).then(function (h) { return String((h && h.model) || '').slice(0, 40); }).catch(function () { return ''; });
            }
        } catch (_) {}
        var m = /;\s*([^;)]+)\s+Build\//.exec(ua);
        return Promise.resolve(m ? m[1].slice(0, 40) : '');
    }

    window.WrDualCamera = {
        isIOS: isIOS, isMobile: isMobile, openSecond: openSecond, probe: probe, watchTrack: watchTrack,
        antiFlicker: antiFlicker, sceneAudioTrack: sceneAudioTrack, remoteHost: remoteHost,
        remoteSender: remoteSender, deviceModel: deviceModel
    };
})();
